import { useTranslation } from 'react-i18next';
import { type SafetyScore } from '@/api/types';
import { headlinePercent, scoreToLevel, type SafetyLevel } from '@/utils/safety';
import { Card } from '@/components/ui/Card';
import { IcShield, IcMapPin } from '@/components/ui/icons';
import { Skeleton } from '@/components/ui/Skeleton';
import styles from './SafetyScoreBanner.module.css';

interface SafetyScoreBannerProps {
  score?: SafetyScore | null;
  locationLabel?: string;
  onLocationClick?: () => void;
  loading?: boolean;
  longSummaryOverride?: string;
  trailing?: React.ReactNode;
  contributingIncidents?: Array<{ id: string; category: string }>;
}

const variantByLevel: Record<SafetyLevel, string> = {
  red: styles.red ?? '',
  yellow: styles.yellow ?? '',
  green: styles.green ?? '',
};

export function SafetyScoreBanner({
  score,
  locationLabel,
  onLocationClick,
  loading,
  longSummaryOverride,
  trailing,
  contributingIncidents,
}: SafetyScoreBannerProps): JSX.Element {
  const { t } = useTranslation();

  if (loading || !score) {
    return (
      <Card variant="light" padding="md">
        <div className={styles.headerRow}>
          <div className={styles.title}>{t('home.situation.title')}</div>
        </div>
        <div className={styles.skeletonRow}>
          <Skeleton width="60%" height={32} />
          <Skeleton width="40%" height={20} />
        </div>
      </Card>
    );
  }

  const level: SafetyLevel = score.level ?? scoreToLevel(score.safety_score);
  const pct = headlinePercent(score.safety_score, level);
  const summary =
    longSummaryOverride ??
    (level === 'green'
      ? t('home.situation.level_green_long')
      : level === 'yellow'
        ? t('home.situation.level_yellow_long')
        : t('home.situation.level_red_long'));

  return (
    <Card variant="light" padding="md">
      <div className={styles.headerRow}>
        <div className={styles.title}>{t('home.situation.title')}</div>
        {locationLabel && (
          <button
            type="button"
            className={styles.locationChip}
            onClick={onLocationClick}
            aria-label={locationLabel}
          >
            <IcMapPin width={16} height={16} />
            <span>{locationLabel}</span>
          </button>
        )}
      </div>

      <div className={`${styles.dangerStrip} ${variantByLevel[level]}`}>
        <div className={styles.stripLeft}>
          <IcShield />
          <div>
            <div className={styles.dangerLabel}>{t('home.situation.danger_percentage')}</div>
            {level === 'green' && (
              <div className={styles.dangerSub}>{t('home.situation.level_green_summary')}</div>
            )}
          </div>
        </div>
        <div className={styles.dangerPercent} data-testid="safety-headline">
          {pct}%
        </div>
      </div>

      <div className={styles.summaryBlock}>
        <div className={styles.summaryTitle}>{t('home.situation.summary')}</div>
        <p className={styles.summaryText}>{summary}</p>
      </div>

      {!!contributingIncidents?.length && (
        <ul className={styles.causes}>
          {contributingIncidents.slice(0, 4).map((i) => (
            <li key={i.id} className={styles.cause}>
              <span className={styles.dot} aria-hidden />
              {t(`triggers.${i.category}`, { defaultValue: i.category })}
            </li>
          ))}
        </ul>
      )}

      {trailing}
    </Card>
  );
}
